/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2021, Zumper
 */

type AlphaNum =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | 'A'
  | 'B'
  | 'C'
  | 'D'
  | 'E'
  | 'F'
  | 'G'
  | 'H'
  | 'I'
  | 'J'
  | 'K'
  | 'L'
  | 'M'
  | 'N'
  | 'O'
  | 'P'
  | 'Q'
  | 'R'
  | 'S'
  | 'T'
  | 'U'
  | 'V'
  | 'W'
  | 'X'
  | 'Y'
  | 'Z'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '_'

type Whitespace =
  | '\f'
  | '\n'
  | '\r'
  | '\t'
  | '\v'
  | '\u0020'
  | '\u00a0'
  | '\u1680'
  | '\u2000'
  | '\u2001'
  | '\u2002'
  | '\u2003'
  | '\u2004'
  | '\u2005'
  | '\u2006'
  | '\u2007'
  | '\u2008'
  | '\u2009'
  | '\u200a'
  | '\u2028'
  | '\u2029'
  | '\u202f'
  | '\u205f'
  | '\u3000'
  | '\ufeff'

export type TitleCase<
  S extends string,
  CapitalizeNext extends boolean = true,
> = S extends `${infer FirstCharacter}${infer SecondCharacter}${infer Rest}`
  ? CapitalizeNext extends true
    ? FirstCharacter extends AlphaNum
      ? `${Capitalize<FirstCharacter>}${SecondCharacter extends Whitespace
          ? `${SecondCharacter}${TitleCase<Rest, true>}`
          : TitleCase<`${SecondCharacter}${Rest}`, false>}`
      : `${FirstCharacter}${TitleCase<`${SecondCharacter}${Rest}`, true>}`
    : `${Lowercase<FirstCharacter>}${SecondCharacter extends Whitespace
        ? `${SecondCharacter}${TitleCase<Rest, true>}`
        : TitleCase<`${SecondCharacter}${Rest}`, false>}`
  : string extends S
    ? string
    : CapitalizeNext extends true
      ? Capitalize<S>
      : Lowercase<S>

export function capitalize(text?: undefined): ''
export function capitalize<S extends string>(text: S): TitleCase<S>
export function capitalize<S extends string | undefined>(
  text?: S
): undefined | TitleCase<NonNullable<S>>
export function capitalize<S extends string | undefined>(
  word?: S
): S extends undefined ? '' : TitleCase<NonNullable<S>> {
  if (!word?.length) {
    // @ts-expect-error TypeScript can't tell that we're doing the right transformation
    return ''
  }
  // @ts-expect-error TypeScript can't tell that we're doing the right transformation
  return word.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase()
  )
}
